import React, { useEffect, useState, useMemo } from "react";
import {
    Box, CssBaseline, Typography, Avatar, IconButton,
    Stack, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, TextField, InputAdornment, Menu, MenuItem,
    TablePagination, Breadcrumbs, Link, CircularProgress,
    Dialog, DialogTitle, Snackbar, Alert
} from '@mui/material';
import {
    Search, MoreVert, Edit, Delete,
    Add, Phone, WarningAmber,
    FilterList
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    accentDark: '#FFA000',
    white: '#FFFFFF'
};

const ParentListScreen = () => {
    const navigate = useNavigate();

    // --- Original State Management ---
    const [parentList, setParentList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterSearch, setFilterSearch] = useState("");
    
    // UI States
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [selectedParent, setSelectedParent] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        getParentList();
        localStorage.setItem("current_tab", "Parent");
    }, []);

    // --- API Logic ---
    const getParentList = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setParentList(resJson.data);
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to connect to server", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const deleteParent = async (parentId) => {
        setLoadingDelete(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents/${parentId}`, {
                method: 'DELETE'
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Parent deleted successfully", severity: "success" });
                setOpenDeleteDialog(false);
                getParentList();
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Delete failed", severity: "error" });
        } finally {
            setLoadingDelete(false);
        }
    };

    // --- Logic Helpers ---
    const handleActionOpen = (event, parent) => {
        setActionAnchorEl(event.currentTarget);
        setSelectedParent(parent);
    };

    const handleActionClose = () => {
        setActionAnchorEl(null);
    };

    // Updated Universal Search Logic
    const filteredData = useMemo(() => {
        return parentList.filter(item => {
            const searchStr = filterSearch.toUpperCase();
            // Check if search string exists in either fullname OR mobileNo
            return (
                (item.fullname && item.fullname.toUpperCase().includes(searchStr)) ||
                (item.mobileNo && item.mobileNo.toUpperCase().includes(searchStr))
            );
        });
    }, [parentList, filterSearch]);

    const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />

            {/* Main Content Area */}
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: colors.white, m: 2, borderRadius: 5 }}>

                {/* Breadcrumbs & Header */}
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate("/Portal")} sx={{ cursor: 'pointer' }}>
                        Portal
                    </Link>
                    <Typography color="text.primary">Parents</Typography>
                </Breadcrumbs>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Header title="Parent Directory" subtitle="Manage guardian information and contact details" />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate("/Portal/Parent/Register")}
                        sx={{ bgcolor: colors.accent, color: colors.textMain, borderRadius: 2, fontWeight: 'bold', '&:hover': { bgcolor: colors.accentDark } }}
                    >
                        Add Parent
                    </Button>
                </Stack>

                {/* Search Bar */}
                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <FilterList sx={{ color: colors.textMain }} />
                        <TextField
                            placeholder="Search by name or mobile number..."
                            size="small"
                            value={filterSearch}
                            onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
                            sx={{ width: 350, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#F8F9FA' } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                        />
                    </Stack>
                </Paper>

                {/* Table */}
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '55vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Parent Name</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Mobile Number</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Location</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Status</TableCell>
                                <TableCell align="right" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={30} color="inherit" />
                                    </TableCell>
                                </TableRow>
                            ) : paginatedData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>No Records Found</TableCell>
                                </TableRow>
                            ) : paginatedData.map((row) => (
                                <TableRow key={row.parentId} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: '#ecd2f3ff', color: '#6a1c7bff', fontSize: 14 }}>
                                                {row.fullname?.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold">{row.fullname}</Typography>
                                                <Typography variant="caption" color="text.secondary">ID: {row.parentId}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Phone fontSize="small" color="action" />
                                            <Typography variant="body2">{row.mobileNo || '-'}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.location === "PD" ? "Pantai Dalam" : "PJCC"}
                                            size="small"
                                            sx={{
                                                borderRadius: 1.5,
                                                bgcolor: row.location === "PD" ? '#d1f6f9' : '#feffeb',
                                                color: row.location === "PD" ? '#2e6c7d' : '#c6a928',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status === "A" ? "Active" : "Inactive"}
                                            size="small"
                                            sx={{
                                                borderRadius: 1.5,
                                                bgcolor: row.status === "A" ? '#E8F5E9' : '#FFEBEE',
                                                color: row.status === "A" ? '#2E7D32' : '#C62828',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={(e) => handleActionOpen(e, row)}><MoreVert /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />

                {/* Action Menu */}
                <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={handleActionClose}>
                    <MenuItem onClick={() => { navigate(`/Portal/Parent/EditParent/${selectedParent?.parentId}`); handleActionClose(); }}>
                        <Edit fontSize="small" sx={{ mr: 1 }} /> Edit Info
                    </MenuItem>
                    <MenuItem onClick={() => { setOpenDeleteDialog(true); handleActionClose(); }} sx={{ color: 'error.main' }}>
                        <Delete fontSize="small" sx={{ mr: 1 }} /> Remove
                    </MenuItem>
                </Menu>

                {/* Delete Dialog */}
                <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
                    <Box p={3} textAlign="center">
                        <WarningAmber sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                        <DialogTitle sx={{ fontWeight: 'bold', p: 0 }}>Confirm Delete</DialogTitle>
                        <Typography variant="body2" sx={{ mt: 1, mb: 3 }}>
                            Are you sure you want to delete <strong>{selectedParent?.fullname}</strong>? This action cannot be undone.
                        </Typography>
                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button onClick={() => setOpenDeleteDialog(false)} color="inherit" variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
                            <Button
                                onClick={() => deleteParent(selectedParent.parentId)}
                                color="error"
                                variant="contained"
                                disabled={loadingDelete}
                                sx={{ borderRadius: 2 }}
                            >
                                {loadingDelete ? <CircularProgress size={20} /> : "Confirm Delete"}
                            </Button>
                        </Stack>
                    </Box>
                </Dialog>

                {/* Feedback Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </Box>
        </Box>
    );
};

export default ParentListScreen;